import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { acceptPartnerInvite, getPartnerInvite } from '../../services/api';
import { clearAuthState } from '../../services/authStorage';
import { ArrowRight } from 'lucide-react';
import { PasswordField } from '../auth/AuthShell';
import { FIRST_BUTTON, FIRST_INPUT, FIRST_LABEL, FirstSessionCard } from '../setup/setupKit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/*
 * The accept page for a Solution Partner invite (/partner/join/:token).
 *
 * Public: the single-use token in the URL is the authorization, the same way
 * a client's team invite works. The partner confirms their name, chooses a
 * password, and is signed straight in to the welcome deck. Nothing about the
 * account travels by email except this link.
 */

type Invite = { name: string; email: string; inviter_name: string; inviter_email: string; expires_at: string };

/* Parked: the join page follows the owner sign-up layout, with Google on the right.
const PartnerSteps: React.FC = () => (
  <div className="rounded-[16px] border border-[#e7e7e4] bg-white p-6">
    <p className="m-0 mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#777982]">What happens next</p>
    <ol className="m-0 flex list-none flex-col gap-4 p-0">
      {[
        ['Choose a password, or use Google', 'Your sign-in from now on. The link stops working once it is used.'],
        ['See what your clients walk', 'Every person at a client starts with a short setup: about them, their apps, what AI may do, and ideas.'],
        ['Add your own sections', 'People and Tools are set for every client. Add your own sections after them.'],
        ['Get your client link', 'Share it and a client sets themselves up under you.'],
      ].map(([title, body], i) => (
        <li key={title} className="flex gap-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#18191d] text-[11px] font-bold text-white">{i + 1}</span>
          <div>
            <div className="text-[13.5px] font-semibold text-[#18191d]">{title}</div>
            <div className="text-[12.5px] text-[#666971]">{body}</div>
          </div>
        </li>
      ))}
    </ol>
  </div>
);
*/

export const PartnerJoin: React.FC = () => {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let live = true;
    getPartnerInvite(token)
      .then((data) => {
        if (!live) return;
        setInvite(data);
        setName(data.name || '');
      })
      .catch((err) => {
        if (!live) return;
        const e = err as { response?: { data?: { detail?: string } } };
        setLoadError(e.response?.data?.detail || 'This invite link is invalid, used, or expired.');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token]);

  const valid = name.trim().length > 0 && password.length >= 8 && password === confirm;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Choose a password of at least 8 characters.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setSubmitting(true);
    setError('');
    try {
      // A stale session from another account must not outlive the accept.
      clearAuthState();
      const data = await acceptPartnerInvite(token, { name: name.trim(), password, phone: phone.trim() || undefined });
      localStorage.setItem('auth_token', data.token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user_role', data.user.role);
      localStorage.setItem('user_id', data.user.id);
      localStorage.setItem('user_email', data.user.email);
      localStorage.setItem('user_name', data.user.name);
      if (data.user.consultant_id) localStorage.setItem('consultant_id', data.user.consultant_id);
      navigate('/partner/welcome', { replace: true });
    } catch (err) {
      const failure = err as { response?: { data?: { detail?: string } } };
      setError(failure.response?.data?.detail || 'Could not finish setting up. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* Google in place of a password: the server takes only the Google account
     for this invite's email, and uses up the link as setting a password does. */
  const continueWithGoogle = () => {
    if (!token || !invite) return;
    const query = new URLSearchParams({ login_hint: invite.email, partner_invite: token });
    if (name.trim()) query.set('name', name.trim());
    if (phone.trim()) query.set('phone', phone.trim());
    clearAuthState();
    window.location.href = `${API_BASE_URL}/api/auth/google?${query.toString()}`;
  };

  if (loading) {
    return (
      <FirstSessionCard title="One moment.">
        <p className="m-0 text-[14px] text-[#666971]">Checking your invite.</p>
      </FirstSessionCard>
    );
  }

  if (!invite) {
    return (
      <FirstSessionCard
        title="This link has stopped working."
        lead={loadError}
        below={<p className="mt-5 text-center text-[13px] text-[#666971]">Already set up? <Link to="/login" className="font-semibold text-[#18191d] underline underline-offset-4">Sign in</Link>.</p>}
      >
        <p className="m-0 text-[14px] text-[#666971]">Invite links work once and expire after 14 days. Ask the person who invited you to send a new one.</p>
      </FirstSessionCard>
    );
  }

  let submitLabel = 'Set my password and continue';
  if (submitting) submitLabel = 'Setting up your console';

  return (
    <FirstSessionCard
      title="Welcome, Solution Partner."
      lead="Confirm your name and choose a password. Then see the setup your clients walk and add your own sections to their intake."
      aside={(
        <div className="flex h-full flex-col justify-center gap-4">
          <div>
            <p className="m-0 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-[#777982]">Faster</p>
            <h2 className="m-0 mt-1 text-[18px] font-bold text-[#18191d]">Sign up with Google</h2>
            <p className="m-0 mt-2 text-[13.5px] leading-[1.55] text-[#666971]">Use the Google account for <b className="text-[#18191d]">{invite.email}</b>, the email you were invited with. No password to remember.</p>
          </div>
          <button type="button" onClick={continueWithGoogle} disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-[10px] border border-[#dedede] bg-white px-4 py-3 text-[14px] font-semibold text-[#18191d] hover:border-[#18191d]">
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>
        </div>
      )}
      below={<p className="mt-5 text-center text-[13px] text-[#666971]">Not you? Ignore this page and nothing changes.</p>}
    >
      <p className="m-0 text-[13.5px] text-[#666971]">Invited by <b className="text-[#18191d]">{invite.inviter_name || 'Agents Anywhere'}</b>. This link works once.</p>
      <form onSubmit={submit} className="space-y-5" noValidate>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="join-email" className={FIRST_LABEL}>Sign-in email</label>
            <input id="join-email" value={invite.email} readOnly className={`${FIRST_INPUT} bg-[#f7f7f5] text-[#666971]`} />
          </div>
          <div>
            <label htmlFor="join-name" className={FIRST_LABEL}>Name</label>
            <input id="join-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" required className={FIRST_INPUT} />
          </div>
        </div>
        <div>
          <label htmlFor="join-phone" className={FIRST_LABEL}>Phone number <span className="font-normal text-[#92949d]">(optional)</span></label>
          <input id="join-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile" className={FIRST_INPUT} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <PasswordField id="join-password" label="Password" value={password} onChange={setPassword} placeholder="8 characters or more" autoComplete="new-password" labelClassName={FIRST_LABEL} inputClassName={FIRST_INPUT} />
          <PasswordField id="join-confirm" label="Confirm password" value={confirm} onChange={setConfirm} placeholder="Type it again" autoComplete="new-password" labelClassName={FIRST_LABEL} inputClassName={FIRST_INPUT} />
        </div>
        {error && <p role="alert" className="m-0 rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">{error}</p>}
        <button type="submit" disabled={submitting || !valid} className={FIRST_BUTTON}>
          {submitLabel} <ArrowRight size={18} />
        </button>
      </form>
    </FirstSessionCard>
  );
};

export default PartnerJoin;
